import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import NoChatImg from "assets/images/noChat.png";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { memo, type RefObject, useCallback, useEffect, useState } from "react";
import type { VirtuosoHandle } from "react-virtuoso";
import { Virtuoso } from "react-virtuoso";
import { AITriageNote } from "shared/features/helenaV2/components/agents/AITriageNote";
import { mapTriageMetadataToNoteProps } from "shared/features/helenaV2/components/agents/triageNoteMapper";
import type { ChatResultItem } from "shared/types";

import { getFullName } from "utilz/helpers";

import { FeedbackCard } from "./FeedbackCard";
import {
  CustomerMessage,
  canEditMessage,
  MemberTimelineBlock,
  ResourceMessage,
} from "./MessageBlocks";
import type { ChatListItem } from "./utils/flattenForVirtuoso";

dayjs.extend(relativeTime);

// ─── CustomerResourceMessage ─────────────────────────────────────────

interface CustomerResourceMessageProps {
  message: ChatResultItem;
  type: "resource" | "customer";
  onMarkAsResolvedClick: () => void;
  onAddToKBClick?: () => void;
  onPinClick?: () => void;
  onReplyClick?: () => void;
  onEditClick?: () => void;
  onThreadClick?: () => void;
  isPinned?: boolean;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  isHighlighted?: boolean;
  isEditing?: boolean;
  onEditSave?: (chatId: string, newContent: string) => Promise<void>;
  onEditCancel?: () => void;
  sentAsEmail?: boolean | null;
  messageSource?: "canned_notes" | "email" | "time_entry_notes" | "normal";
}

const CustomerResourceMessage = memo(
  ({
    message,
    type,
    onMarkAsResolvedClick,
    onAddToKBClick,
    onPinClick,
    onReplyClick,
    onEditClick,
    onThreadClick,
    isPinned,
    isFirstInGroup,
    isLastInGroup,
    isHighlighted,
    isEditing,
    onEditSave,
    onEditCancel,
    sentAsEmail,
    messageSource,
  }: CustomerResourceMessageProps) => {
    if (type === "resource") {
      return (
        <ResourceMessage
          type={message.access_specifier}
          isMarkedAsResolved={message.mark_as_resolved}
          onMarkAsResolvedClick={onMarkAsResolvedClick}
          onAddToKBClick={onAddToKBClick}
          onPinClick={onPinClick}
          onReplyClick={onReplyClick}
          onEditClick={onEditClick}
          onThreadClick={onThreadClick}
          isPinned={isPinned}
          message={message.message}
          date={message.created_at}
          attachments={message.attachments}
          senderDisplayName={getFullName(
            message.sender.first_name,
            "",
            message.sender.last_name,
          )}
          imageUrl={message.sender.profile_pic}
          accessSpecifier={message.access_specifier}
          isEdited={message?.editing?.is_edited}
          messagePlatform={message.message_platform}
          isFirstInGroup={isFirstInGroup}
          isLastInGroup={isLastInGroup}
          isHighlighted={isHighlighted}
          excerpt={message.excerpt}
          excerptSenderName={message.excerpt_sender_name}
          excerptCreatedAt={message.excerpt_created_at}
          threadReplyCount={message.thread_reply_count}
          isEditing={isEditing}
          onEditSave={onEditSave}
          onEditCancel={onEditCancel}
          chatId={message.chat_id}
          sentAsEmail={sentAsEmail}
          messageSource={messageSource}
        />
      );
    }
    return (
      <CustomerMessage
        type={message.access_specifier}
        isMarkedAsResolved={message.mark_as_resolved}
        onMarkAsResolvedClick={onMarkAsResolvedClick}
        senderDisplayName={getFullName(
          message.sender.first_name,
          "",
          message.sender.last_name,
        )}
        imageUrl={message.sender.profile_pic}
        message={message.message}
        attachments={message.attachments}
        accessSpecifier={message.access_specifier}
        isEdited={message?.editing?.is_edited}
        date={message.created_at}
        messagePlatform={message.message_platform}
        onAddToKBClick={onAddToKBClick}
        onPinClick={onPinClick}
        onReplyClick={onReplyClick}
        onEditClick={onEditClick}
        onThreadClick={onThreadClick}
        isPinned={isPinned}
        isFirstInGroup={isFirstInGroup}
        isLastInGroup={isLastInGroup}
        isHighlighted={isHighlighted}
        excerpt={message.excerpt}
        excerptSenderName={message.excerpt_sender_name}
        excerptCreatedAt={message.excerpt_created_at}
        threadReplyCount={message.thread_reply_count}
        isEditing={isEditing}
        onEditSave={onEditSave}
        onEditCancel={onEditCancel}
        chatId={message.chat_id}
        sentAsEmail={sentAsEmail}
        messageSource={messageSource}
        senderUserType={message.sender.user_type}
        isHelenaUser={message.is_helena_user}
      />
    );
  },
);

// ─── Helper ──────────────────────────────────────────────────────────

function getMessageType(
  message: ChatResultItem,
  resourceId: string,
): "resource" | "customer" {
  if (message.sender.mapping_id !== resourceId) return "customer";
  const platform = message.message_platform;
  if (platform === "web_app" || platform === "resource_mobile_app") {
    return "resource";
  }
  return "customer";
}

// ─── NoChatScreen ────────────────────────────────────────────────────

const NoChatScreen = () => {
  const theme = useTheme();
  return (
    <Stack
      sx={{
        height: "100%",
        width: "100%",
        justifyContent: "center",
        alignItems: "center",
        gap: "24px",
      }}
    >
      <img
        src={NoChatImg}
        alt="No messages"
        style={{
          width: "50%",
          height: "50%",
          objectFit: "contain",
        }}
      />
      <Typography variant="subtitle1" color={theme.palette.lightBg.low}>
        You're about to start a new conversation
      </Typography>
    </Stack>
  );
};

// ─── DateSeparator ───────────────────────────────────────────────────

const DateSeparator = memo(({ date }: { date: string }) => {
  const theme = useTheme();
  return (
    <Box sx={{ paddingX: "15%" }}>
      <Divider
        sx={{
          ...theme.typography["body-small"],
          color: theme.palette.lightBg.low,
        }}
      >
        {date !== "Today" && date !== "Yesterday"
          ? dayjs(date).format("DD MMM YYYY")
          : date}
      </Divider>
    </Box>
  );
});

// ─── MessageRow (avoids inline closures in renderItem) ──────────────

interface MessageRowProps {
  item: ChatListItem & { kind: "message" };
  resourceId: string;
  isPinned: boolean;
  isHighlighted: boolean;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  isEditing: boolean;
  onMarkAsResolved: (chatId: string, newValue: boolean, key: string) => void;
  onPinMessage?: (message: ChatResultItem, isPinned: boolean) => void;
  onAddToKB?: (
    messageHtml: string,
    attachments?: Array<{
      file_name: string;
      file_type: string;
      url: string;
    }>,
  ) => void;
  onReply: (message: ChatResultItem) => void;
  onThreadClick?: (message: ChatResultItem) => void;
  onEditClick?: (message: ChatResultItem) => void;
  onEditSave?: (chatId: string, newContent: string) => Promise<void>;
  onEditCancel?: () => void;
}

const MessageRow = memo(
  ({
    item,
    resourceId,
    isPinned,
    isHighlighted,
    isFirstInGroup,
    isLastInGroup,
    isEditing,
    onMarkAsResolved,
    onPinMessage,
    onAddToKB,
    onReply,
    onThreadClick,
    onEditClick,
    onEditSave,
    onEditCancel,
  }: MessageRowProps) => {
    const message = item.message;

    const pinHandler = useCallback(
      () => onPinMessage?.(message, isPinned),
      [onPinMessage, message, isPinned],
    );
    const markAsResolvedHandler = useCallback(
      () =>
        onMarkAsResolved(
          message.chat_id,
          !message.mark_as_resolved,
          message.created_at,
        ),
      [
        onMarkAsResolved,
        message.chat_id,
        message.mark_as_resolved,
        message.created_at,
      ],
    );
    const addToKBHandler = useCallback(
      () => onAddToKB?.(message.message, message.attachments),
      [onAddToKB, message.message, message.attachments],
    );
    const replyHandler = useCallback(
      () => onReply(message),
      [onReply, message],
    );
    const threadClickHandler = useCallback(
      () => onThreadClick?.(message),
      [onThreadClick, message],
    );
    const editClickHandler = useCallback(
      () => onEditClick?.(message),
      [onEditClick, message],
    );

    const [editCheckTimestamp, setEditCheckTimestamp] = useState(Date.now());

    // Force re-render after 60 seconds to hide edit button
    useEffect(() => {
      const elapsed = Date.now() - new Date(message.created_at).getTime();
      const remaining = 60000 - elapsed;

      if (remaining <= 0) return;

      const timer = setTimeout(() => {
        setEditCheckTimestamp(Date.now());
      }, remaining);

      return () => clearTimeout(timer);
    }, [message.created_at]);

    // Re-check editability (the editCheckTimestamp ensures re-check after timer fires)
    const editable = editCheckTimestamp && canEditMessage(message, resourceId);

    const messageContent = (() => {
      if (
        message.message_type === "triage_agent_response" &&
        message.triage_agent_metadata
      ) {
        return (
          <Box sx={{ py: "8px", ml: "32px" }}>
            <AITriageNote
              {...mapTriageMetadataToNoteProps(message.triage_agent_metadata)}
            />
          </Box>
        );
      }

      if (message.message_type === "member_timeline") {
        return (
          <MemberTimelineBlock
            sender={message.sender}
            timelineMeta={message.timeline_meta}
            resourceId={resourceId}
          />
        );
      }

      return (
        <CustomerResourceMessage
          message={message}
          type={getMessageType(message, resourceId)}
          onMarkAsResolvedClick={markAsResolvedHandler}
          onAddToKBClick={onAddToKB ? addToKBHandler : undefined}
          onPinClick={onPinMessage ? pinHandler : undefined}
          onReplyClick={replyHandler}
          onEditClick={editable ? editClickHandler : undefined}
          onThreadClick={threadClickHandler}
          isPinned={isPinned}
          isFirstInGroup={isFirstInGroup}
          isLastInGroup={isLastInGroup}
          isHighlighted={isHighlighted}
          isEditing={isEditing}
          onEditSave={onEditSave}
          onEditCancel={onEditCancel}
          sentAsEmail={message.send_as_email}
          messageSource={
            message.message_platform === "email"
              ? "email"
              : ((message.message_type ?? "normal") as
                  | "canned_notes"
                  | "time_entry_notes"
                  | "normal")
          }
        />
      );
    })();

    return (
      <Box
        data-chat-id={message.chat_id}
        sx={{
          display: "flex",
          flexDirection: "column",
          px: "24px",
        }}
      >
        {messageContent}
      </Box>
    );
  },
);

// ─── ChatMessageList ─────────────────────────────────────────────────

interface ChatMessageListProps {
  flatChatList: ChatListItem[];
  firstItemIndex: number;
  virtuosoRef: RefObject<VirtuosoHandle>;
  isMessagesLoading: boolean;
  resourceId: string;
  pinnedChatIdSet: Set<string>;
  feedback?: string | null;
  rating?: number | null;
  onMarkAsResolved: (chatId: string, newValue: boolean, key: string) => void;
  onPinMessage?: (message: ChatResultItem, isPinned: boolean) => void;
  onAddToKB?: (
    messageHtml: string,
    attachments?: Array<{
      file_name: string;
      file_type: string;
      url: string;
    }>,
  ) => void;
  onReply: (message: ChatResultItem) => void;
  onThreadClick?: (message: ChatResultItem) => void;
  onLoadMore: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  highlightedChatId: string | null;
  editingMessageId?: string | null;
  onEditClick?: (message: ChatResultItem) => void;
  onEditSave?: (chatId: string, newContent: string) => Promise<void>;
  onEditCancel?: () => void;
  onAtBottomStateChange?: (atBottom: boolean) => void;
}

export const ChatMessageList = memo(
  ({
    flatChatList,
    firstItemIndex,
    virtuosoRef,
    isMessagesLoading,
    resourceId,
    pinnedChatIdSet,
    feedback,
    rating,
    onMarkAsResolved,
    onPinMessage,
    onAddToKB,
    onReply,
    onThreadClick,
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
    highlightedChatId,
    editingMessageId,
    onEditClick,
    onEditSave,
    onEditCancel,
    onAtBottomStateChange,
  }: ChatMessageListProps) => {
    const theme = useTheme();

    const renderItem = useCallback(
      (_index: number, item: ChatListItem) => {
        if (item.kind === "date-separator") {
          return (
            <Box sx={{ px: "24px" }}>
              <DateSeparator date={item.date} />
            </Box>
          );
        }

        return (
          <MessageRow
            item={item}
            resourceId={resourceId}
            isPinned={pinnedChatIdSet.has(item.message.chat_id)}
            isHighlighted={highlightedChatId === item.message.chat_id}
            isFirstInGroup={item.isFirstInGroup}
            isLastInGroup={item.isLastInGroup}
            isEditing={editingMessageId === item.message.chat_id}
            onMarkAsResolved={onMarkAsResolved}
            onPinMessage={onPinMessage}
            onAddToKB={onAddToKB}
            onReply={onReply}
            onThreadClick={onThreadClick}
            onEditClick={onEditClick}
            onEditSave={onEditSave}
            onEditCancel={onEditCancel}
          />
        );
      },
      [
        pinnedChatIdSet,
        highlightedChatId,
        editingMessageId,
        onPinMessage,
        onMarkAsResolved,
        onAddToKB,
        onReply,
        onThreadClick,
        onEditClick,
        onEditSave,
        onEditCancel,
        resourceId,
      ],
    );

    const Header = useCallback(
      () => (
        <Box sx={{ pt: "48px" }}>
          {isFetchingNextPage && (
            <Stack alignItems="center" py="16px">
              <CircularProgress size={24} />
            </Stack>
          )}
        </Box>
      ),
      [isFetchingNextPage],
    );

    const Footer = useCallback(
      () => (
        <Box sx={{ pb: "48px", px: "48px" }}>
          {rating ? <FeedbackCard feedback={feedback} rating={rating} /> : null}
        </Box>
      ),
      [feedback, rating],
    );

    const EmptyPlaceholder = useCallback(
      () => (isMessagesLoading ? null : <NoChatScreen />),
      [isMessagesLoading],
    );

    const handleStartReached = useCallback(() => {
      if (hasNextPage && !isFetchingNextPage) {
        onLoadMore();
      }
    }, [hasNextPage, isFetchingNextPage, onLoadMore]);

    if (isMessagesLoading && flatChatList.length === 0) {
      return (
        <Stack
          sx={{
            flexGrow: 1,
            justifyContent: "center",
            alignItems: "center",
            padding: "48px",
            borderRight: `0px solid ${theme.palette.neutral.main}`,
            borderLeft: `0px solid ${theme.palette.neutral.main}`,
          }}
        >
          <CircularProgress size={32} />
        </Stack>
      );
    }

    return (
      <Virtuoso
        ref={virtuosoRef}
        data={flatChatList}
        firstItemIndex={firstItemIndex}
        computeItemKey={(_index, item) => item.key}
        initialTopMostItemIndex={
          flatChatList.length > 0 ? flatChatList.length - 1 : 0
        }
        followOutput="smooth"
        alignToBottom
        skipAnimationFrameInResizeObserver
        increaseViewportBy={{ top: 800, bottom: 200 }}
        startReached={handleStartReached}
        atBottomStateChange={onAtBottomStateChange}
        atBottomThreshold={100}
        itemContent={renderItem}
        components={{
          Header,
          Footer,
          EmptyPlaceholder,
        }}
        style={{
          flexGrow: 1,
          width: "100%",
        }}
      />
    );
  },
);
